import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { MentionInput, Review } from '@skop-app/lib-domain/types'
import { ReviewStatus } from '@skop-app/lib-types'
import { v4 as uuid } from 'uuid'

import { useUserContext } from '@/context/UserContext'
import type { ReviewInteractionMode, ReviewMessageParams } from '@/types'
import { getBugsnag } from '@/utils/bugsnag.client'
import { trackUsageEvent } from '@/utils/posthog.utils'
import { buildReviewMetadata } from '@/utils/reviews'
import { WriteMaintenanceModeError } from '@/services/writeMaintenance'

import {
  applyOptimisticReviewChange,
  applyOptimisticReviewMessage,
  cancelReviewQueries,
  collectReviewMutationContext,
  restoreReviewMutationContext,
  revalidateReviewQueries,
  type ReviewMutationContext,
} from './reviewCache'

type SubmitReviewInteractionCallbacks = {
  onOptimisticApplied?: () => void
  onRollback?: () => void
}

export type PreparedReviewInteraction = {
  interactionId: string
  mode: ReviewInteractionMode
  reviewId: string
  previousReview?: Partial<Review>
  nextReview: Partial<Review>
  reviewMode: 'create' | 'update' | null
  messageInput: {
    id: string
    reviewID: string
    userID: string
    status: 'PENDING'
    message: string
  }
  optimisticMessageInput: {
    id: string
    reviewID: string
    userID: string
    status: 'PENDING'
    message: string
    createdAt: string
    updatedAt: string
  }
  mentionsInput: MentionInput[]
}

type SubmitReviewInteractionVariables = {
  prepared: PreparedReviewInteraction
  callbacks?: SubmitReviewInteractionCallbacks
  interactionKey: string
}

type SubmitReviewInteractionMutationContext = {
  rollbackContext?: ReviewMutationContext
  callbacks?: SubmitReviewInteractionCallbacks
  interactionKey: string
}

const inFlightReviewInteractions = new Set<string>()

const getReviewInteractionKey = ({
  review,
  target,
}: Pick<ReviewMessageParams, 'review' | 'target'>) => {
  if (review?.id) return `review:${review.id}`
  if (!target) return 'review:unknown'

  return `target:${target.reviewType}:${target.elementID}:${target.fieldKey}:${target.colId}`
}

const inferReviewInteractionMode = ({
  review,
  mode,
}: Pick<ReviewMessageParams, 'review' | 'mode'>): ReviewInteractionMode => {
  if (mode) return mode
  if (!review?.id) return 'create'
  return review.status === ReviewStatus.PENDING ? 'reply' : 'reopen'
}

export const submitReviewInteractionRequest = async (
  prepared: PreparedReviewInteraction,
) => {
  const response = await fetch('/api/reviews/interaction', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(prepared),
  })

  if (!response.ok) {
    const errorType = response.headers?.get('X-Skop-Error-Type')
    if (errorType === 'MaintenanceMode') {
      throw new WriteMaintenanceModeError()
    }
    const message = await response.text()
    throw new Error(message || 'Failed to submit review interaction')
  }
}

const reportReviewInteractionFailure = ({
  error,
  prepared,
  interactionKey,
}: {
  error: unknown
  prepared: PreparedReviewInteraction
  interactionKey: string
}) => {
  try {
    const bugsnag = getBugsnag()
    const issue =
      error instanceof Error
        ? error
        : new Error('Failed to submit review interaction')

    bugsnag?.notify(issue, (event) => {
      event.addMetadata('reviewInteraction', {
        interactionKey,
        mode: prepared.mode,
        reviewMode: prepared.reviewMode,
        reviewId: prepared.reviewId,
        reviewType: prepared.nextReview.type,
        elementID: prepared.nextReview.elementID,
        organizationID: prepared.nextReview.organizationID,
        previousStatus: prepared.previousReview?.status,
        nextStatus: prepared.nextReview.status,
        mentionsCount: prepared.mentionsInput.length,
      })
    })
  } catch {}
}

const prepareReviewInteraction = ({
  review,
  message,
  mentions = [],
  target,
  mode,
  interactionId,
  userId,
}: ReviewMessageParams & {
  userId: string
}): PreparedReviewInteraction => {
  const now = new Date().toISOString()
  const trimmedMessage = message.trim()
  const nextMode = inferReviewInteractionMode({ review, mode })
  const reviewId = review?.id ?? uuid()
  const messageId = uuid()
  const mentionsInput = mentions.map((mention) => ({
    id: uuid(),
    userMentionsId: mention.value.toString(),
    messageMentionsId: messageId,
    role: mention.role,
  }))

  if (nextMode === 'create') {
    const reviewType = review?.type ?? target?.reviewType
    const elementID = review?.elementID ?? target?.elementID
    const organizationID = review?.organizationID ?? target?.organizationID
    const metadata =
      review?.metadata && review.metadata.length > 0
        ? review.metadata
        : target
          ? buildReviewMetadata(target)
          : []

    if (!reviewType || !elementID || !organizationID || !metadata.length) {
      throw new Error('Missing review target')
    }

    return {
      interactionId: interactionId ?? uuid(),
      mode: nextMode,
      reviewId,
      previousReview: review,
      reviewMode: 'create',
      nextReview: {
        ...review,
        id: reviewId,
        type: reviewType,
        status: review?.status ?? ReviewStatus.PENDING,
        elementID,
        byUserID: review?.byUserID ?? userId,
        organizationID,
        metadata,
        createdAt: review?.createdAt ?? now,
      },
      messageInput: {
        id: messageId,
        reviewID: reviewId,
        userID: userId,
        status: 'PENDING',
        message: trimmedMessage,
      },
      optimisticMessageInput: {
        id: messageId,
        reviewID: reviewId,
        userID: userId,
        status: 'PENDING',
        message: trimmedMessage,
        createdAt: now,
        updatedAt: now,
      },
      mentionsInput,
    }
  }

  if (!review) {
    throw new Error('Missing review for interaction')
  }

  const previousReview = review
    ? ({
        id: review.id,
        status: review.status,
        organizationID: review.organizationID,
      } as Partial<Review>)
    : undefined
  const reviewMode = nextMode === 'reopen' ? ('update' as const) : null
  const nextReview =
    nextMode === 'reopen'
      ? {
          ...review,
          status: ReviewStatus.PENDING,
        }
      : (review as Review)

  return {
    interactionId: interactionId ?? uuid(),
    mode: nextMode,
    reviewId,
    previousReview,
    reviewMode,
    nextReview,
    messageInput: {
      id: messageId,
      reviewID: reviewId,
      userID: userId,
      status: 'PENDING',
      message: trimmedMessage,
    },
    optimisticMessageInput: {
      id: messageId,
      reviewID: reviewId,
      userID: userId,
      status: 'PENDING',
      message: trimmedMessage,
      createdAt: now,
      updatedAt: now,
    },
    mentionsInput,
  }
}

export const useSubmitReviewInteraction = () => {
  const queryClient = useQueryClient()
  const { user } = useUserContext()

  const { isPending, mutateAsync: submitReviewInteractionMutation } =
    useMutation<
      void,
      unknown,
      SubmitReviewInteractionVariables,
      SubmitReviewInteractionMutationContext
    >({
      mutationKey: ['submitReviewInteraction'],
      mutationFn: async ({ prepared }) => {
        await submitReviewInteractionRequest(prepared)
      },
      onMutate: async ({
        prepared,
        callbacks,
        interactionKey,
      }): Promise<SubmitReviewInteractionMutationContext> => {
        const drawerQueryMode =
          prepared.reviewMode === null ? 'existingOnly' : 'scoped'

        await cancelReviewQueries({
          queryClient,
          review: prepared.nextReview,
          previousReview: prepared.previousReview,
          reviewId: prepared.reviewId,
          includeDetail: true,
          includeMessages: true,
          includeDrawer: true,
          includeGrid: true,
          includeCount: Boolean(prepared.reviewMode),
          drawerQueryMode,
        })

        const rollbackContext = collectReviewMutationContext({
          queryClient,
          review: prepared.nextReview,
          previousReview: prepared.previousReview,
          reviewId: prepared.reviewId,
          includeDetail: true,
          includeMessages: true,
          includeDrawer: true,
          includeGrid: true,
          includeCount: Boolean(prepared.reviewMode),
          drawerQueryMode,
        })

        if (prepared.reviewMode) {
          applyOptimisticReviewChange({
            queryClient,
            mode: prepared.reviewMode,
            input: prepared.nextReview,
          })
        }

        applyOptimisticReviewMessage({
          queryClient,
          reviewId: prepared.reviewId,
          message: prepared.optimisticMessageInput,
          mentions: prepared.mentionsInput,
          user,
          seedReview: prepared.nextReview as Review,
        })

        callbacks?.onOptimisticApplied?.()

        return {
          rollbackContext,
          callbacks,
          interactionKey,
        }
      },
      onError: (error, variables, context) => {
        reportReviewInteractionFailure({
          error,
          prepared: variables.prepared,
          interactionKey: context?.interactionKey ?? variables.interactionKey,
        })
        trackUsageEvent('feature_used', {
          feature: 'review',
          action: 'submit_review_interaction',
          surface: 'review_workflow',
          result: 'failure',
        })
        restoreReviewMutationContext({
          queryClient,
          context: context?.rollbackContext,
        })
        context?.callbacks?.onRollback?.()
      },
      onSuccess: async (_data, { prepared }) => {
        const drawerQueryMode =
          prepared.reviewMode === null ? 'existingOnly' : 'scoped'

        await revalidateReviewQueries({
          queryClient,
          review: prepared.nextReview,
          previousReview: prepared.previousReview,
          reviewId: prepared.reviewId,
          includeDetail: true,
          includeMessages: true,
          includeDrawer: true,
          includeGrid: true,
          includeCount: Boolean(prepared.reviewMode),
          includeNotifications: true,
          refetchDetail: true,
          drawerQueryMode,
        })
        trackUsageEvent('feature_used', {
          feature: 'review',
          action: 'submit_review_interaction',
          surface: 'review_workflow',
          result: 'success',
        })
      },
      onSettled: (_data, _error, variables, context) => {
        inFlightReviewInteractions.delete(
          context?.interactionKey ?? variables.interactionKey,
        )
      },
    })

  const submitInteraction = async (
    params: ReviewMessageParams,
    callbacks?: SubmitReviewInteractionCallbacks,
  ) => {
    if (!user?.id || !params.message.trim()) return

    const prepared = prepareReviewInteraction({
      ...params,
      userId: user.id,
    })
    const interactionKey = getReviewInteractionKey(params)
    if (inFlightReviewInteractions.has(interactionKey)) {
      return
    }
    inFlightReviewInteractions.add(interactionKey)

    try {
      await submitReviewInteractionMutation({
        prepared,
        callbacks,
        interactionKey,
      })
    } catch (error) {
      inFlightReviewInteractions.delete(interactionKey)
      throw error
    }
  }

  return {
    submitInteraction,
    isPending,
  }
}
